"use client";

import "./globals.css";
import { useEffect } from "react";
import { RefreshCw } from "lucide-react";
import { BrandMark } from "@/components/brand-lockup";
import { Button } from "@/components/ui/button";
import { captureClientError } from "@/lib/posthog-client";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // PostHog's `capture_exceptions` autocapture only listens for
  // `window.onerror` and `unhandledrejection`. React error boundaries
  // intercept the throw before it reaches either, so without an explicit
  // capture here root-layout crashes would be invisible in Error Tracking.
  useEffect(() => {
    captureClientError(error, "app.global-error", {
      digest: error.digest ?? null,
    });
  }, [error]);

  return (
    <html lang="en">
      <body className="bg-background text-foreground min-h-svh font-sans antialiased">
        <main className="mx-auto flex min-h-svh w-full max-w-md flex-col px-4 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          <BrandMark />
          <div className="flex flex-1 flex-col justify-center gap-3 py-10">
            <h1 className="text-title">Something went wrong</h1>
            <p className="text-body text-secondary-foreground">
              Couldn&apos;t load this page. Try again, and if it keeps
              happening, contact your administrator.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Button onClick={reset} size="lg" className="h-12 w-full">
              <RefreshCw />
              Try again
            </Button>
            {/* A document load, so a broken client state starts fresh. */}
            <Button
              variant="ghost"
              size="lg"
              onClick={() => window.location.assign("/")}
              className="h-12 w-full"
            >
              Go to the start page
            </Button>
          </div>
        </main>
      </body>
    </html>
  );
}
